import { useEffect } from 'react';
import { Range, type BaseRange } from 'slate';
import type { SlateEditorType } from '@builder4/shared/types/text';
import { editorRegistry } from '@/components/userComponents/Text';

export type SelectionChangePayload = {
  editorId: string;
  editor: SlateEditorType;
  selection: BaseRange | null;
  previousSelection: BaseRange | null;
};

type SelectionListener = (payload: SelectionChangePayload) => void;

const listeners = new Set<SelectionListener>();
const detachMap = new Map<string, () => void>();
const pendingNotifications = new Map<string, number>();

const areSelectionsEqual = (a: BaseRange | null, b: BaseRange | null) => {
  if (a === b) return true;
  if (!a || !b) return a === b;
  return Range.equals(a, b);
};

export const ensureSlateSelectionObserver = (editorId: string, editor: SlateEditorType) => {
  removeSlateSelectionObserver(editorId);

  const originalOnChange = editor.onChange;

  const handleChange: typeof editor.onChange = (...args) => {
    const previousSelection = editorRegistry.get(editorId)?.selection ?? null;

    originalOnChange?.(...args);

    const nextSelection = editor.selection ?? null;

    if (!areSelectionsEqual(previousSelection, nextSelection)) {
      editorRegistry.set(editorId, { editor, selection: nextSelection });
      const payload: SelectionChangePayload = {
        editorId,
        editor,
        selection: nextSelection,
        previousSelection,
      };

      const existingTimeout = pendingNotifications.get(editorId);
      if (existingTimeout !== undefined) {
        window.clearTimeout(existingTimeout);
      }

      const timeoutId = window.setTimeout(() => {
        pendingNotifications.delete(editorId);
        listeners.forEach((listener) => listener(payload));
      }, 200);

      pendingNotifications.set(editorId, timeoutId);
    }
  };

  editor.onChange = handleChange;

  detachMap.set(editorId, () => {
    editor.onChange = originalOnChange;
    detachMap.delete(editorId);
  });
};

export const removeSlateSelectionObserver = (editorId: string) => {
  const detach = detachMap.get(editorId);
  if (detach) {
    detach();
  }

  const pendingTimeout = pendingNotifications.get(editorId);
  if (pendingTimeout !== undefined) {
    window.clearTimeout(pendingTimeout);
    pendingNotifications.delete(editorId);
  }
};

export const useSlateSelectionChange = (listener: SelectionListener) => {
  useEffect(() => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, [listener]);
};
